import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Center,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Spinner,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MultiSelect } from 'react-multi-select-component';
import { Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FaInfoCircle } from 'react-icons/fa';
import { AiFillInfoCircle, FiTrash } from 'react-icons/all';
import Select from 'react-select';
import { Button, Heading, Table } from '~/components';
import { SelectItem } from '~/types';
import { MultiSelectStrings } from '~/utils/multiSelectStrings';
import {
  useEligibleContracts,
  useGroupZeroCarbonProjects,
} from '~/hooks/useZeroCarbon/useGroupZeroCarbon';
import {
  postPreviewAddContract,
  solicitarCertificado,
} from '~/services/GroupZeroCarbon';
import {
  PreviewAddContractView,
  ZeroCarbonProjectView,
} from '~/types/zeroCarbon';
import { queryClient } from '~/utils/react-query';
import { formatCurrency } from '~/utils/formatCurrency';

const ApplyForCertificateFormSchema = yup.object().shape({
  contratos: yup.array().min(1, 'Selecione ao menos um contrato'),
  projeto: yup.string().required('Projeto obrigatório'),
});

export const ApplyForCertificateDialog: React.FC<{ id_grupo: string }> = ({
  id_grupo,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { formState, getValues, setValue, setError, clearErrors } = useForm({
    resolver: yupResolver(ApplyForCertificateFormSchema),
  });
  const { errors } = formState;
  const { data: projects, refetch: refetchProjects } =
    useGroupZeroCarbonProjects();
  const {
    data: contracts,
    refetch: refetchContracts,
    isLoading: isLoadingContracts,
  } = useEligibleContracts(id_grupo, {
    onSuccess: data => {
      setListaContratos(
        data.results.map(contract => ({
          label: `${contract.nu_contrato} | ${
            contract.nu_pedido
          } | Saldo a compensar: ${formatCurrency(
            Number(contract.saldo_co2_kg),
          ).replace('R$', '')}kg`,
          value: `${contract.id_contrato}`,
        })),
      );
    },
  });
  const [listaContratos, setListaContratos] = useState<SelectItem[]>([]);
  const [contratosSelecionados, setContratosSelecionados] = useState<
    SelectItem[]
  >([]);
  const [previewContratos, setPreviewContratos] = useState<
    PreviewAddContractView | undefined
  >(undefined);
  const [projetoSelecionado, setProjetoSelecionado] = useState<string>('');
  const [isFetching, setIsFetching] = useState(false);

  const handleClose = () => {
    setContratosSelecionados([]);
    setProjetoSelecionado('');
    setPreviewContratos(undefined);
    clearErrors();
    onClose();
  };

  const handleOpen = () => {
    onOpen();
    setValue('contratos', []);
    setValue('projeto', '');
    refetchContracts();
    refetchProjects();
  };

  useEffect(() => {
    const ctt = contracts?.results.filter(x => {
      return contratosSelecionados.some(y => Number(y.value) === x.id_contrato);
    });
    const fctt = ctt?.map(x => {
      return {
        id_contrato: x.id_contrato,
        id_adendoaditivo: x.id_adendoaditivo,
      };
    });
    postPreviewAddContract(fctt || [])
      .then(data => {
        setPreviewContratos(data);
      })
      .catch(err => {
        toast({
          title: 'Erro',
          description: err.response.data.detail,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      });
  }, [contratosSelecionados]);

  const formInvalido = () => {
    const { contratos, projeto } = getValues();
    return !projeto || !contratos || contratos.length === 0;
  };

  const salvar = () => {
    if (formInvalido()) {
      toast({
        title: 'Erro',
        description: 'Preencha todos os campos obrigatórios',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }
    const data = {
      contratos: contratosSelecionados.map(x => ({
        id_contrato: Number(x.value),
        id_adendoaditivo:
          contracts?.results.find(y => y.id_contrato === Number(x.value))
            ?.id_adendoaditivo || undefined,
      })),
      id_projeto_co2: Number(projetoSelecionado),
      id_grupo_economico: Number(id_grupo),
    };
    setIsFetching(true);
    solicitarCertificado(data)
      .then(() => {
        toast({
          title: 'Solicitação enviada com sucesso',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries([
          'group_certificates_zero_carbon',
          id_grupo,
        ]);
        queryClient.invalidateQueries([
          'group_zero_carbon_statistics',
          id_grupo,
        ]);
        handleClose();
      })
      .catch(err => {
        toast({
          title: 'Erro ao enviar solicitação',
          description: err.response.data.detail,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsFetching(false);
      });
  };

  const handleSelectedContractChange = (changes: SelectItem[]) => {
    clearErrors('contratos');
    if (changes.length === 0) {
      setError('contratos', { message: 'Selecione ao menos um contrato' });
    }
    setValue('contratos', changes);
    setContratosSelecionados(changes);
  };

  const removeSelecionado = (id: number) => {
    const newContratosSelecionados = contratosSelecionados.filter(
      x => Number(x.value) !== id,
    );
    if (newContratosSelecionados.length === 0) {
      setError('contratos', { message: 'Selecione ao menos um contrato' });
    }
    setValue('contratos', newContratosSelecionados);
    setContratosSelecionados(newContratosSelecionados);
  };

  const selectedProject = projects?.find(
    x => x.id === Number(projetoSelecionado),
  );

  const templateOptions = (project: ZeroCarbonProjectView) => {
    return {
      value: project.id,
      label: (
        <HStack justifyContent="space-between" mb={1}>
          <Stack>
            <Text title={project.nome} m={0} p={0} lineHeight={1}>
              {project.nome.length > 80
                ? `${project.nome.slice(0, 80)}...`
                : project.nome}
            </Text>
            <Text
              color="blackAlpha.600"
              fontSize="13px"
              m={0}
              p={0}
              lineHeight={1}
            >
              <b>Quantidade de CO2e disponível:</b>{' '}
              {project.formattedQuantidadeCo2Disponivel} tCO2e
            </Text>
          </Stack>
          {previewContratos &&
            Number(previewContratos.total_a_ser_compensado_t) >
              Number(project.quantidade_co2_disponivel) && (
              <HStack color="red.500" gridGap={2} whiteSpace="nowrap">
                <Icon as={AiFillInfoCircle} />
                <Text>Projeto sem crédito CO2e suficiente</Text>
              </HStack>
            )}
        </HStack>
      ),
    };
  };

  const options = projects?.map(projeto =>
    templateOptions(projeto as ZeroCarbonProjectView),
  );

  const value = () => {
    const projeto = projects?.find(x => x.id === Number(projetoSelecionado));
    return projeto
      ? templateOptions(projeto as ZeroCarbonProjectView)
      : undefined;
  };

  return (
    <>
      <Button variant="primary" size="sm" onClick={handleOpen}>
        Solicitar certificado
      </Button>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="xl"
        closeOnOverlayClick={false}
      >
        <DrawerOverlay />
        <DrawerContent sx={{ maxWidth: '75vw', width: '75vw' }}>
          <DrawerCloseButton />
          <DrawerHeader mb={4}>
            <Heading fontSize="xl">Solicitar Certificado</Heading>
          </DrawerHeader>

          <DrawerBody>
            <Stack gridGap={3}>
              <FormControl width="100%" isInvalid={errors.contratos}>
                <FormLabel fontSize="14px">
                  Contratos <span style={{ color: 'red' }}>*</span>
                </FormLabel>
                <MultiSelect
                  value={contratosSelecionados}
                  isLoading={isLoadingContracts}
                  options={listaContratos}
                  onChange={(changes: SelectItem[]) =>
                    handleSelectedContractChange(changes)
                  }
                  labelledBy="Selecione"
                  overrideStrings={{
                    ...MultiSelectStrings,
                    selectAll: 'Contrato | Termo/Pedido de Locação',
                  }}
                  disabled={isFetching}
                />
              </FormControl>
              <TableContainer>
                <Table type="secondary">
                  <Thead>
                    <Tr>
                      <Th>N° CONTRATO SAP</Th>
                      <Th>N° DO TERMO</Th>
                      <Th textAlign="end">QTDE DE ITENS</Th>
                      <Th textAlign="end">PRAZO</Th>
                      <Th textAlign="end">
                        <HStack textAlign="end">
                          <Text>CONSUMO INICIAL (CO2E)</Text>
                          <Tooltip
                            hasArrow
                            label="Consumo inicial (kg CO2e) * Quantidade de equipamentos"
                            bg="black"
                            rounded="md"
                            placement="top"
                            shouldWrapChildren
                          >
                            <Icon
                              as={FaInfoCircle}
                              boxSize={4}
                              color="gray.900"
                            />
                          </Tooltip>
                        </HStack>
                      </Th>
                      <Th textAlign="end">
                        <HStack textAlign="end">
                          <Text>CONSUMO MENSAL (CO2E)</Text>
                          <Tooltip
                            hasArrow
                            label="Consumo mensal (kg CO2e/mês) * Quantidade de equipamentos"
                            bg="black"
                            rounded="md"
                            placement="top"
                            shouldWrapChildren
                          >
                            <Icon
                              as={FaInfoCircle}
                              boxSize={4}
                              color="gray.900"
                            />
                          </Tooltip>
                        </HStack>
                      </Th>
                      <Th textAlign="end">
                        <HStack justifyContent="end">
                          <Text>CONSUMO TOTAL (CO2E)</Text>
                          <Tooltip
                            hasArrow
                            label="Consumo inicial + (Consumo mensal * Prazo)"
                            bg="black"
                            rounded="md"
                            placement="top"
                            shouldWrapChildren
                          >
                            <Icon
                              as={FaInfoCircle}
                              boxSize={4}
                              color="gray.900"
                            />
                          </Tooltip>
                        </HStack>
                      </Th>
                      <Th textAlign="end">SALDO A COMPENSAR</Th>
                      <Th>AÇÃO</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {!!previewContratos && previewContratos.preview.length ? (
                      previewContratos.preview.map(it => (
                        <Tr key={it.nu_contrato}>
                          <Td>{it.nu_contrato}</Td>
                          <Td>{it.nu_pedido}</Td>
                          <Td textAlign="end">{it.quantidade_itens}</Td>
                          <Td textAlign="end">{it.prazo}</Td>
                          <Td textAlign="end">
                            {it.formattedConsumoInicialKg}kg
                          </Td>
                          <Td textAlign="end">
                            {it.formattedConsumoMensalKg}kg
                          </Td>
                          <Td textAlign="end">
                            {it.formattedConsumoTotalKg}kg
                          </Td>
                          <Td textAlign="end">
                            {it.formattedConsumoDisponivelKg}kg
                          </Td>
                          <Td>
                            <IconButton
                              onClick={() => removeSelecionado(it.id_contrato)}
                              aria-label="Remover"
                              icon={<FiTrash />}
                              isDisabled={isFetching}
                            />
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={7} textAlign="center">
                          {' '}
                          Nenhum contrato selecionado.
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
              <HStack
                gridGap={5}
                divider={
                  <Center height="60px">
                    <Divider orientation="vertical" />
                  </Center>
                }
              >
                <Stack gridGap={2} w="30%">
                  <Text fontSize="15px" fontWeight="bold">
                    Total consumido:
                  </Text>
                  <HStack alignItems="baseline" gridGap={0}>
                    <Heading>
                      {previewContratos?.formattedTotalConsumidoKg || '- '}kg
                    </Heading>
                  </HStack>
                </Stack>
                <Stack gridGap={2} w="30%">
                  <Text fontSize="15px" fontWeight="bold">
                    Total a ser compensado:
                  </Text>
                  <HStack alignItems="baseline" gridGap={0}>
                    <Heading>
                      {previewContratos?.formattedTotalASerCompensadoT || '- '}t
                    </Heading>
                  </HStack>
                </Stack>
                <Stack gridGap={2} w="30%">
                  <Text fontSize="15px" fontWeight="bold">
                    Saldo:
                  </Text>
                  <HStack alignItems="baseline" gridGap={0}>
                    <Heading>
                      {previewContratos?.formattedSaldoCo2Kg || '- '}kg
                    </Heading>
                  </HStack>
                </Stack>
              </HStack>
              <FormControl width="100%" isInvalid={errors.projeto}>
                <FormLabel fontSize="14px">
                  Projetos <span style={{ color: 'red' }}>*</span>
                </FormLabel>
                <Select
                  value={value()}
                  options={options}
                  placeholder="Selecione"
                  onChange={e => {
                    clearErrors('projeto');
                    !e?.value &&
                      setError('projeto', { message: 'Projeto obrigatório' });
                    setValue('projeto', e?.value);
                    setProjetoSelecionado(e ? e.value.toString() : '');
                  }}
                  isDisabled={isFetching}
                />
              </FormControl>
              {!!projetoSelecionado && !selectedProject?.possui_carta && (
                <Alert
                  bg="#f7f2fa"
                  borderRadius="md"
                  color="#9246ce"
                  fontSize="sm"
                  py={1}
                  px={2}
                >
                  <AlertDescription>
                    <b>Atenção:</b> Não existe{' '}
                    <b>&quot;Carta Zero Carbon&quot;</b> cadastrada para esse
                    projeto. Cadastre antes de enviar o certificado para o
                    cliente.
                  </AlertDescription>
                </Alert>
              )}
            </Stack>
          </DrawerBody>

          <DrawerFooter>
            <Button variant="secondary" onClick={() => handleClose()}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={salvar}
              ms={2}
              isDisabled={isFetching}
            >
              Enviar solicitação {isFetching && <Spinner />}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};
